import React, { useState, useEffect } from 'react';
import axiosInstance from '../config/axios';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import Navbar from './Navbar';
import CreateUserModal from './CreateUserModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import './UsersList.css';

const UsersList = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Delete modal states
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    userId: null,
    userName: '',
    loading: false
  });

  useEffect(() => {
    if (user?.is_admin) {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/api/users');
      setUsers(response.data.users);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId, userName) => {
    setDeleteModal({
      isOpen: true,
      userId,
      userName,
      loading: false
    });
  };

  const handleDeleteConfirm = async () => {
    const { userId } = deleteModal;
    
    setDeleteModal(prev => ({ ...prev, loading: true }));
    
    try {
        await axiosInstance.delete(`/api/users/${userId}`);
      setDeleteModal({ isOpen: false, userId: null, userName: '', loading: false });
      fetchUsers();
      setError('');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erreur lors de la suppression';
      setError(errorMsg);
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, userId: null, userName: '', loading: false });
  };

  const handleUserCreated = () => {
    setShowCreateModal(false);
    fetchUsers();
  };

  if (!user?.is_admin) {
    return (
      <div className="users-list">
        <Navbar />
        <div className="error-message">Accès non autorisé</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="users-list">
        <Navbar />
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="users-list">
      <Navbar />
      <div className="users-content">
        <div className="users-header">
          <div>
            <h1>Utilisateurs</h1>
            <p>{users.length} utilisateur(s) au total</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="create-user-button"
          >
            + Créer un utilisateur
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom d'utilisateur</th>
                <th>Email</th>
                <th>Admin</th>
                <th>Date de création</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    Aucun utilisateur
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td>
                      {u.is_admin ? (
                        <span className="admin-badge">Admin</span>
                      ) : (
                        <span className="user-badge">Utilisateur</span>
                      )}
                    </td>
                    <td>{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                    <td>
                      {u.id !== user.id && (
                        <button
                          onClick={() => handleDeleteClick(u.id, u.username)}
                          className="delete-button"
                        >
                          Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleUserCreated}
        />
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message="Êtes-vous sûr de vouloir supprimer cet utilisateur ?"
        itemName={deleteModal.userName ? `Utilisateur: ${deleteModal.userName}` : ''}
        loading={deleteModal.loading}
      />
    </div>
  );
};

export default UsersList;

